import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { observer } from 'mobx-react-lite'

import { getPreparedScanData } from 'pages/tours/scan-document-page/lib/getPreparedScanData.ts'

import { ScanMedia } from 'features/scan-media'

import formTouristStore from 'entities/tour/models/form-tourist/form-tourist.ts'

import { IScanDocument, ScanDocument } from 'shared/api/scan-document'
import { TYPE_AGE } from 'shared/constants'
import { getErrorMessage } from 'shared/lib/errorText'
import citizenshipStore from 'shared/store/citizenship-store'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { EmptyPage } from 'shared/ui/empty-page'
import { HeaderGoBack } from 'shared/ui/header-go-back'
import { Loader } from 'shared/ui/loader'

import {
  DialogScanPassport,
  DialogScanPassportError,
  ScanPage,
} from './components'
import s from './styles.module.scss'

const ScanPassport = observer(() => {
  const { t } = useTranslation()

  const navigate = useNavigate()

  const { openDialog } = useDialogBottomSheet()

  const [loading, setLoading] = useState<boolean>(false)
  const [showCamera, setShowCamera] = useState<boolean>(false)

  const [searchParams] = useSearchParams()

  const indexPassenger = searchParams.get('passenger')

  const agePassenger = searchParams.get('age') as
    | TYPE_AGE.ADULTS
    | TYPE_AGE.CHILDREN

  useEffect(() => {
    void citizenshipStore.ensureTourCitizenships()
  }, [])

  const hasCitizenshipsLoadError =
    citizenshipStore.citizenshipTour === null &&
    citizenshipStore.tourStatus === 'error'
  const isCitizenshipsInitialLoading =
    citizenshipStore.citizenshipTour === null && !hasCitizenshipsLoadError

  const openCamera = async () => {
    setShowCamera(true)
  }

  const makePhoto = async (photo?: File) => {
    if (!photo) {
      onShowError()

      return
    }

    setLoading(true)
    setShowCamera(false)

    try {
      const formData = new FormData()

      formData.append('file', photo)

      const { data: scanResult } = await ScanDocument.scanUserDocument(formData)

      openDialog({
        isDialog: false,
        child: (
          <DialogScanPassport
            data={scanResult.data}
            onTryAgainScan={retryCamera}
            onContinue={() => onSubmitScanDocument(scanResult.data)}
          />
        ),
      })
    } catch (error) {
      const errorText = getErrorMessage(error)

      onShowError(errorText)
    } finally {
      setLoading(false)
    }
  }

  const onShowError = (errorText?: string) => {
    openDialog({
      isDialog: false,
      child: (
        <DialogScanPassportError
          text={errorText || t('scan_passport.error_scaning_password')}
        />
      ),
    })
  }

  const onSubmitScanDocument = (data: IScanDocument) => {
    if (!indexPassenger || !agePassenger) return null

    const preparedScanData = getPreparedScanData(data)

    formTouristStore.setPassengerData({
      index: Number(indexPassenger),
      age: agePassenger,
      scannedData: preparedScanData,
    })

    navigate(-1)
  }

  const onErrorShowCamera = () => {
    setShowCamera(false)

    openDialog({
      isDialog: false,
      child: <DialogScanPassportError text={t('scan_passport.error_open')} />,
    })
  }

  const retryCamera = () => {
    setShowCamera(true)
  }

  return (
    <>
      {showCamera && (
        <ScanMedia
          onMakeShot={makePhoto}
          onError={onErrorShowCamera}
          mask={<div className={s.crop} />}
        />
      )}

      <div className="container">
        <div className={s.header}>
          <HeaderGoBack
            title={t('scan_passport.how_to_scan')}
            buttonWhite
          />
        </div>

        {loading || isCitizenshipsInitialLoading ? (
          <Loader marginTop={150} />
        ) : hasCitizenshipsLoadError ? (
          <EmptyPage
            title={t('error')}
            subTitle={t('error_des')}
            buttonText={t('try_again')}
            onClick={() => void citizenshipStore.ensureTourCitizenships()}
          />
        ) : (
          <ScanPage openCamera={openCamera} />
        )}
      </div>
    </>
  )
})

export default ScanPassport
